import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

import I18n from '@wetransfer/transfer/modules/i18n';
import Helper from '@wetransfer/transfer/helper';
import Callout from '@wetransfer/transfer/component/Callout/Callout';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';

import './PortalsCallout.scss';
import TeamDiscoveryExperiment from '@wetransfer/transfer/constant/experiments/team-discovery-experiment';
import Analytics from '@wetransfer/transfer/modules/analytics';
import { Button } from '@wetransfer/transfer/oneui/react';
import { State } from '@wetransfer/transfer/store/types';

import portalsVideo from './assets/portals-animation.mp4';
import Fade from './Fade';

interface Props {
  calloutStyles: React.CSSProperties;
}

export const PortalsCallout: React.FC<Props> = ({ calloutStyles }) => {
  const [isCalloutOpen, setIsCalloutOpen] = useState(true);
  const [show, setShow] = useState(false);

  const animationDuration = 300;
  const calloutRef = useRef(null);

  const hasBeenSeenOnce = Cookie.get(
    AppConfig.cookies.portalsCalloutSeenOnce.name
  );

  const { domain } = useSelector((state: State) => ({
    domain: state.session.domain,
  }));

  useEffect(() => {
    setShow(true);
  }, []);

  function readCookie() {
    const cookie = Cookie.get(AppConfig.cookies.portalsCalloutSeenOnce.name);

    if (!cookie) {
      return { date: new Date(), transferCount: 0 };
    }

    // Examine cookie
    const hasSeenFirstPortalsCallout = cookie.split(',');
    const [date, numberOfTransfers] = hasSeenFirstPortalsCallout;

    const transferCount = parseInt(numberOfTransfers, 10);

    return { date, transferCount };
  }

  function checkCookieData() {
    const previousData = readCookie();

    // How many transfers have been sent?
    const sent = Helper.getWTSent();
    // How many transfers have been done since?
    const transferCountBetweenDates = sent - previousData.transferCount;

    const numberOfDaysSinceLastTransfer = Helper.getDaysBetweenDates(
      previousData.date,
      new Date()
    );

    const showPortalCalloutDefault =
      !TeamDiscoveryExperiment.isShowTeamDiscoveryExperiment() &&
      !Cookie.get(AppConfig.cookies.portalsCalloutSeen.name) &&
      !Cookie.get(AppConfig.cookies.portalsOnboarded.name) &&
      Number(Cookie.get(AppConfig.cookies.teamsOnboardingCallout.name)) === 999;

    return {
      showPortalCalloutDefault,
      transferCountBetweenDates,
      numberOfDaysSinceLastTransfer,
    };
  }

  function displayForFirstTime(): boolean {
    const cookieData = checkCookieData();

    const teamsOnboardingCalloutCookieDate = Cookie.get(
      AppConfig.cookies.teamsOnboardingCalloutDate.name
    );

    if (teamsOnboardingCalloutCookieDate) {
      const teamsOnboardingCookieDetails =
        teamsOnboardingCalloutCookieDate.split(',');
      const [onboardingDate] = teamsOnboardingCookieDetails;

      const numberOfDaysPassed = Helper.getDaysBetweenDates(
        onboardingDate,
        new Date()
      );

      //Condition based on the cookie date set when the user sees the branding callout
      //It has been set before the release of the portals callout for people who will be new Pro users.
      // We want to leave 1 day between the branding and the portals callout display.
      //This doesn't apply to users who have already seen the branding callout before the release
      if (onboardingDate) {
        return (
          cookieData.showPortalCalloutDefault &&
          !Cookie.get(AppConfig.cookies.portalsCalloutSeenOnce.name) &&
          numberOfDaysPassed >= 1
        );
      }
    }

    return (
      cookieData.showPortalCalloutDefault &&
      !Cookie.get(AppConfig.cookies.portalsCalloutSeenOnce.name)
    );
  }

  function displayForSecondTime(): boolean {
    const cookieData = checkCookieData();

    return (
      cookieData.showPortalCalloutDefault &&
      hasBeenSeenOnce &&
      (cookieData.transferCountBetweenDates >= 3 ||
        cookieData.numberOfDaysSinceLastTransfer >= 14)
    );
  }

  function handlePortalsCalloutClick() {
    Analytics.trackSnowplowEvent({
      category: 'portals_post_transfer_callout',
      action: 'click_try_portals',
      label: hasBeenSeenOnce ? 'second_viewing' : 'first_viewing',
    });

    if (!Cookie.get(AppConfig.cookies.portalsOnboarded.name)) {
      Cookie.set(AppConfig.cookies.portalsOnboarded.name, 1, { days: 365 });
    }

    const portalsUrl = Helper.getPortalsUrl();
    window.location.assign(portalsUrl);

    dismissPortalsCallout();
  }

  function dismissCtaTracking(label: string): void {
    Analytics.trackSnowplowEvent({
      category: 'portals_post_transfer_callout',
      action: 'click_not_now',
      label: label,
    });
  }

  function dismissPortalsCallout() {
    setShow(false);

    if (hasBeenSeenOnce) {
      dismissCtaTracking('second_viewing');
    } else {
      dismissCtaTracking('first_viewing');
    }

    setTimeout(() => {
      if (!hasBeenSeenOnce) {
        const now = new Date();

        const formattedDate = `${
          now.getMonth() + 1
        }/${now.getDate()}/${now.getFullYear()}`;
        const sentTransfers = Helper.getWTSent();
        const cookieValue = [formattedDate, sentTransfers].join(',');

        Cookie.set(AppConfig.cookies.portalsCalloutSeenOnce.name, cookieValue, {
          days: 365,
          domain,
        });
      } else {
        Cookie.unset(AppConfig.cookies.portalsCalloutSeenOnce.name);
        Cookie.set(AppConfig.cookies.portalsCalloutSeen.name, 'true', {
          days: 365,
          domain,
        });
      }
      setIsCalloutOpen(false);
    }, animationDuration);
  }

  function portalTracking() {
    Analytics.trackSnowplowEvent({
      category: 'portals_post_transfer_callout',
      action: 'show_callout',
      label: hasBeenSeenOnce ? 'second_viewing' : 'first_viewing',
    });
  }

  const portalsCalloutFirstViewing = (
    <Callout
      isOpen={isCalloutOpen}
      calloutStyles={calloutStyles}
      data-testid="portals-callout-first-viewing"
      callback={portalTracking}
      ref={calloutRef}
    >
      <h2
        className="PortalsCallout__title"
        dangerouslySetInnerHTML={{
          __html: I18n.t('page.modal.portals_intro.title'),
        }}
      />
      <video
        src={portalsVideo}
        title="Portals"
        className="PortalsCallout__video"
        autoPlay
        muted
        loop
      />
      <p
        className="PortalsCallout__text-content"
        dangerouslySetInnerHTML={{
          __html: I18n.t('page.modal.portals_intro.text'),
        }}
      />
      <Button
        label={I18n.t('callout.portals-onboarding.first-viewing.cta')}
        variant="primary"
        stroke={true}
        square={true}
        onClick={handlePortalsCalloutClick}
      />
      <Button
        label={I18n.t('callout.portals-onboarding.first-viewing.dismiss')}
        small={true}
        link={true}
        onClick={dismissPortalsCallout}
      />
    </Callout>
  );

  const portalsCalloutSecondViewing = (
    <Callout
      ref={calloutRef}
      isOpen={isCalloutOpen}
      calloutStyles={calloutStyles}
      data-testid="portals-callout-second-viewing"
      callback={portalTracking}
    >
      <h2 className="PortalsCallout__title">
        {I18n.t('callout.portals-onboarding.second-viewing.title')}
      </h2>
      <video
        src={portalsVideo}
        title="Portals"
        className="PortalsCallout__video"
        autoPlay
        muted
        loop
      />
      <p
        className="PortalsCallout__text-content"
        dangerouslySetInnerHTML={{
          __html: I18n.t('callout.portals-onboarding.second-viewing.text'),
        }}
      />
      <Button
        label={I18n.t('callout.portals-onboarding.second-viewing.cta')}
        variant="primary"
        stroke={true}
        square={true}
        onClick={handlePortalsCalloutClick}
      />
      <Button
        label={I18n.t('callout.portals-onboarding.second-viewing.dismiss')}
        small={true}
        link={true}
        onClick={dismissPortalsCallout}
      />
    </Callout>
  );

  return (
    <Fade show={show} className="PortalsCallout">
      {displayForFirstTime() && portalsCalloutFirstViewing}
      {displayForSecondTime() && portalsCalloutSecondViewing}
    </Fade>
  );
};

export default PortalsCallout;
